import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  /** @type {AbortController} */
  #controller;

  /**
   * Whether the default open state has already been applied once. Morph re-renders can move this
   * element via insertBefore, which disconnects and reconnects it even though it's the same instance.
   * Without this guard, connectedCallback would re-apply the default (closed) state on every such
   * move, discarding whatever open/closed state the user had set by interacting with the accordion.
   */
  #hasInitialized = false;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    if (!this.#hasInitialized) {
      this.#setDefaultOpenState();
      this.#hasInitialized = true;
    }
    this.#enforceSingleOpen();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    this.details.addEventListener('toggle', this.#handleToggle, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    // Section "open on desktop" (`.accordion--open-on-desktop`) uses CSS pointer-events: none on mouse; keyboard / AT
    // can still activate <summary>. Only lock in that mode — mere `open-by-default-on-desktop` must allow collapse (e.g. facets).
    if (isDesktop && this.#locksOpenOnDesktop()) {
      event.preventDefault();
      this.details.open = true;
    }
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
    this.#enforceSingleOpen();
  };

  /**
   * When desktop lock is active (Accordion section setting), prevent closing via any code path so state matches visuals.
   */
  #handleToggle = () => {
    if (!isMobileBreakpoint() && this.#locksOpenOnDesktop() && !this.details.open) {
      queueMicrotask(() => {
        this.details.open = true;
      });
      return;
    }

    if (this.details.open) {
      this.#closeSiblingAccordions();
    }
  };

  /** @returns {HTMLElement | null} */
  #getSingleOpenGroup() {
    const group = this.closest('[data-accordion-single-open]');
    return group instanceof HTMLElement ? group : null;
  }

  /** Ensures only one row stays open when multiple rows are open (e.g. default-open settings). */
  #enforceSingleOpen() {
    const group = this.#getSingleOpenGroup();
    if (!group) return;

    const openDetails = [...group.querySelectorAll('accordion-custom > details[open]')];
    openDetails.slice(1).forEach((details) => {
      details.open = false;
    });
  }

  /** Closes other accordion rows in the same single-open group. */
  #closeSiblingAccordions() {
    const group = this.#getSingleOpenGroup();
    if (!group) return;

    group.querySelectorAll('accordion-custom').forEach((accordion) => {
      if (accordion === this || !(accordion instanceof AccordionCustom)) return;

      if (accordion.details.open) {
        accordion.details.open = false;
      }
    });
  }

  /** Accordion section "always expanded on desktop" — summary non-interactive in CSS + must stay open in JS */
  #locksOpenOnDesktop() {
    return this.closest('.accordion--open-on-desktop') !== null;
  }

  /** Initial expanded state when entering desktop breakpoint (still user-collapsible unless `#locksOpenOnDesktop`) */
  #defaultsOpenOnDesktop() {
    return this.hasAttribute('open-by-default-on-desktop') || this.#locksOpenOnDesktop();
  }

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.#defaultsOpenOnDesktop());
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu (skip when desktop always-open keeps the panel expanded)
    if (event.key === 'Escape' && this.#closeWithEscape) {
      if (!isMobileBreakpoint() && this.#locksOpenOnDesktop()) return;

      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
      return;
    }

    // pointer-events: none does not block Space/Enter on <summary>; stop native toggle when desktop lock is active.
    if (
      !isMobileBreakpoint() &&
      this.#locksOpenOnDesktop() &&
      (event.key === ' ' || event.key === 'Enter')
    ) {
      const target = event.target;
      if (!(target instanceof Node) || !this.summary.contains(target)) return;

      event.preventDefault();
      this.details.open = true;
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
